<template>
  <!-- The classic header's "My events" dropdown (Events/SwitchEvent.vue), moved onto the event name
       in the design-system sidebar. Open it is the name and date as before, plus a chevron;
       collapsed it is the events illustration in the rail. The list opens beside the rail, teleported so
       the sidebar's scrolling nav does not clip it. Outside an event it names the organisation
       (SN-069), and its Organisation row leads there from any event. -->
  <Tooltip
    :description="event ? event.name : companyName"
    :tooltip-class="!collapsed || open ? 'hidden' : ''"
    width="w-max"
    placement="right"
    teleport
    class="w-full justify-start"
  >
    <button
      ref="trigger"
      type="button"
      class="flex w-full items-center rounded-(--ds-radius-md) text-left transition-colors duration-150"
      :class="[
        collapsed
          ? 'h-[46px] justify-center'
          : 'gap-2 overflow-hidden px-2 py-1.5',
        open ? 'bg-(--ds-surface-brand)' : 'hover:bg-(--ds-color-grey-50)',
      ]"
      :aria-label="collapsed ? collapsedLabel : undefined"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="dialog"
      data-test="event-switcher-trigger"
      @click="toggle"
    >
      <!-- The same illustration as the "Events" entry at organisation level. -->
      <img
        v-if="collapsed"
        :src="
          Vapor.asset(sidebarIllustration(event ? 'events' : 'organisation'))
        "
        alt=""
        class="size-7 flex-none"
        data-test="event-switcher-icon"
      />
      <!-- eslint-disable vue/no-v-html -- the static design-system icons -->
      <template v-else>
        <!-- Sized to the open sidebar (w-64, less the nav's 2 x 11px, the button's 2 x 8px, the gap
             and the chevron): while the sidebar widens from the rail, the name is clipped instead of
             wrapping letter by letter into a column that pushes the menu down. -->
        <span v-if="event" class="w-[calc(16rem-62px)] flex-none">
          <span
            class="block break-words text-body font-semibold text-(--ds-text-primary)"
            v-text="event.name"
          />
          <span
            class="mt-0.5 block text-caption text-(--ds-text-secondary)"
            v-text="customFormat(event.starts_at, 'MMMM dd, yyyy')"
          />
        </span>
        <!-- The organisation's name is the text; "Organisation" only its caption. -->
        <span
          v-else
          class="w-[calc(16rem-62px)] flex-none"
          data-test="event-switcher-organisation-trigger"
        >
          <span
            class="block text-caption text-(--ds-text-secondary)"
            data-test="event-switcher-caption"
            v-text="$t('common.organisation')"
          />
          <span
            class="mt-0.5 block break-words text-ui font-semibold text-(--ds-text-primary)"
            data-test="event-switcher-name"
            v-text="companyName"
          />
        </span>
        <span
          class="flex size-4 flex-none text-(--ds-text-secondary) transition-transform duration-150 [&>svg]:size-full"
          :class="open && 'rotate-180'"
          aria-hidden="true"
          v-html="ICONS['chevron-down']"
        />
      </template>
      <!-- eslint-enable vue/no-v-html -->
    </button>
  </Tooltip>

  <Teleport to="body">
    <div
      v-if="open"
      ref="panel"
      role="dialog"
      :aria-label="
        $t(canListEvents ? 'common.my_events' : 'common.organisation')
      "
      class="fixed z-60 flex flex-col rounded-(--ds-radius-md) border border-(--ds-border-app) bg-(--ds-surface-default) p-2 shadow-(--ds-elevation-overlay)"
      :style="panelPosition"
      data-test="event-switcher-panel"
    >
      <div v-if="canListEvents" class="relative mb-2.5">
        <!-- eslint-disable vue/no-v-html -- the static design-system icon -->
        <span
          class="pointer-events-none absolute top-1/2 left-2.5 flex size-4 -translate-y-1/2 text-(--ds-text-secondary) [&>svg]:size-full"
          aria-hidden="true"
          v-html="ICONS.search"
        />
        <!-- eslint-enable vue/no-v-html -->
        <input
          id="event-switcher-search"
          ref="searchInput"
          v-model="search"
          type="search"
          autocomplete="off"
          class="h-9 w-full rounded-(--ds-radius-md) border border-(--ds-border-app) bg-(--ds-surface-default) pr-2.5 pl-8 text-ui text-(--ds-text-primary) placeholder:text-(--ds-text-muted) focus:border-(--ds-border-brand) focus:outline-none"
          :placeholder="$t('event.search_event')"
          :aria-label="$t('event.search_event')"
          data-test="event-switcher-search"
        />
      </div>

      <!-- The way to the organisation level, above the events; current outside an event. Users
           who work for more than one organisation switch it from the same row ("Wechseln", the
           user menu's dialog), so the way between organisations sits with the way between events. -->
      <div
        v-if="organisationHref !== null || canSwitchOrganisation"
        class="flex items-center gap-1 rounded-(--ds-radius-md) transition-colors duration-150"
        :class="
          event === null
            ? 'bg-(--ds-surface-brand)'
            : 'hover:bg-(--ds-color-grey-50)'
        "
      >
        <component
          :is="organisationHref !== null ? Link : 'div'"
          ref="organisationRow"
          :href="organisationHref ?? undefined"
          class="flex min-w-0 flex-1 flex-col px-2 py-2"
          :aria-current="
            organisationHref !== null && event === null ? 'true' : undefined
          "
          data-test="event-switcher-organisation"
          @click="organisationHref !== null && close()"
        >
          <!-- Like an event row: the name first, "Organisation" below it where an event has its date. -->
          <span
            class="truncate text-ui font-medium"
            :class="
              event === null
                ? 'text-(--ds-text-brand)'
                : 'text-(--ds-text-primary)'
            "
            v-text="companyName"
          />
          <span
            class="text-caption text-(--ds-text-secondary)"
            v-text="$t('common.organisation')"
          />
        </component>
        <button
          v-if="canSwitchOrganisation"
          type="button"
          class="mr-1.5 flex-none rounded-(--ds-radius-sm) px-2 py-1 text-caption font-semibold text-(--ds-text-brand) transition-colors duration-150 hover:bg-(--ds-surface-brand) hover:underline"
          :aria-label="$t('common.switch_organisation')"
          :title="$t('common.switch_organisation')"
          data-test="event-switcher-switch-organisation"
          @click="openOrganisationSwitcher"
          v-text="$t('common.switch')"
        />
      </div>

      <template v-if="canListEvents">
        <p
          role="heading"
          aria-level="2"
          class="px-2 pt-2 pb-1 text-[11px] font-semibold tracking-[0.08em] text-(--ds-text-secondary) uppercase"
          v-text="$t('common.my_events')"
        />

        <p
          v-if="loading && events.length === 0"
          class="px-2 py-4 text-center text-caption text-(--ds-text-secondary)"
          data-test="event-switcher-loading"
          v-text="$t('common.loading')"
        />
        <p
          v-else-if="filteredEvents.length === 0"
          class="px-2 py-4 text-center text-caption text-(--ds-text-secondary)"
          data-test="event-switcher-empty"
          v-text="$t('common.no_results_found')"
        />
        <ul
          v-else
          class="flex max-h-[360px] min-h-0 flex-col gap-0.5 overflow-y-auto"
        >
          <li v-for="item in filteredEvents" :key="item.id">
            <Link
              :href="useRoute('events.cockpit', { event: item.id })"
              class="flex flex-col rounded-(--ds-radius-md) px-2 py-2 transition-colors duration-150"
              :class="
                item.id === event?.id
                  ? 'bg-(--ds-surface-brand)'
                  : 'hover:bg-(--ds-color-grey-50)'
              "
              :aria-current="item.id === event?.id ? 'true' : undefined"
              data-test="event-switcher-item"
              @click="close"
            >
              <span
                class="text-ui font-medium"
                :class="
                  item.id === event?.id
                    ? 'text-(--ds-text-brand)'
                    : 'text-(--ds-text-primary)'
                "
                v-text="item.name"
              />
              <span
                class="text-caption text-(--ds-text-secondary)"
                v-text="formatDate(item.starts_at, item.timezone)"
              />
            </Link>
          </li>
        </ul>
      </template>
    </div>
  </Teleport>

  <SuspenseWrapper v-if="showOrganisationSwitcher">
    <SwitchCompanyModal @close="showOrganisationSwitcher = false" />
  </SuspenseWrapper>
</template>

<script setup>
import SuspenseWrapper from '@/Components/common/SuspenseWrapper.vue'
import Tooltip from '@/Components/common/Tooltip.vue'
import SwitchCompanyModal from '@/Components/Users/SwitchCompanyModal.vue'
import { customFormat, formatDate } from '@/composables/date'
import { useRoute } from '@/composables/route'
import { useEcho } from '@/composables/useEcho'
import { getJson } from '@/composables/useFetch'
import { ICONS } from '@/Libraries/design-system-icons'
import { sidebarIllustration } from '@/Libraries/sidebarIllustrations'
import { Link, router, usePage } from '@inertiajs/vue3'
import Vapor from 'laravel-vapor'
import { onClickOutside } from '@vueuse/core'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  /** The current event (page prop `event`), or null at organisation level. */
  event: {
    type: Object,
    default: null,
  },
  /** The sidebar is the icon rail: the switcher is the events illustration and opens beside it. */
  collapsed: {
    type: Boolean,
    default: false,
  },
  /** event:list for an active company: the search and the event list. Without it only the Organisation row. */
  canListEvents: {
    type: Boolean,
    default: true,
  },
  /** Where the Organisation row leads (the first organisation entry the user may open), or null for no row. */
  organisationHref: {
    type: String,
    default: null,
  },
})

const { t } = useI18n()

const companyName = computed(
  () => usePage().props._user.active_company?.name ?? '',
)

const collapsedLabel = computed(() =>
  props.event
    ? `${t('common.my_events')}: ${props.event.name}`
    : `${t('common.organisation')}: ${companyName.value}`,
)

const open = ref(false)

/** More than one organisation (company) to work in: the switcher offers to change it. */
const canSwitchOrganisation = computed(
  () => (usePage().props._companies?.length ?? 0) > 1,
)
const showOrganisationSwitcher = ref(false)

const openOrganisationSwitcher = () => {
  open.value = false
  showOrganisationSwitcher.value = true
}
const trigger = ref(null)
const panel = ref(null)
const searchInput = ref(null)
const organisationRow = ref(null)
const panelPosition = ref({})

const events = ref([])
const search = ref('')
const loading = ref(false)

let latestRequest = 0

/**
 * All of the company's events, the same list the classic header's dropdown shows (events.search).
 * Only the latest request is applied: a slower answer to an earlier refresh never replaces a newer one.
 */
const getEvents = async () => {
  const request = ++latestRequest
  loading.value = true

  try {
    const { data } = await getJson(useRoute('events.search'))

    if (request === latestRequest) {
      events.value = data.value?.events ?? []
    }
  } catch {
    // A failed refresh keeps the list as it was; the next opening tries again.
  } finally {
    if (request === latestRequest) {
      loading.value = false
    }
  }
}

/**
 * The search filters the loaded list, so it answers as you type and an empty search shows every
 * event again. It matches as events.search does: the name contains the whole term or any one word.
 */
const filteredEvents = computed(() => {
  const term = search.value.trim().toLowerCase()

  if (term === '') {
    return events.value
  }

  const terms = [term, ...term.split(/\s+/)]

  return events.value.filter((item) => {
    const name = item.name.toLowerCase()

    return terms.some((part) => name.includes(part))
  })
})

const PANEL_WIDTH = 300
const SCREEN_MARGIN = 8

/**
 * Below the event name while the sidebar is open, beside the rail while it is collapsed. On a phone
 * it narrows and shifts to stay on screen, and its list scrolls above the on-screen keyboard.
 */
const placePanel = () => {
  if (trigger.value === null) {
    return
  }

  const rect = trigger.value.getBoundingClientRect()
  const width = Math.min(PANEL_WIDTH, window.innerWidth - 2 * SCREEN_MARGIN)
  const top = props.collapsed ? rect.top : rect.bottom + 6
  const left = Math.min(
    props.collapsed ? rect.right + 12 : rect.left,
    window.innerWidth - width - SCREEN_MARGIN,
  )

  panelPosition.value = {
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
    maxHeight: `${window.innerHeight - top - SCREEN_MARGIN}px`,
  }
}

const onResize = () => open.value && placePanel()

const close = () => {
  open.value = false
}

const toggle = async () => {
  if (open.value) {
    close()
    return
  }

  placePanel()
  open.value = true

  // The list is loaded on mount and kept current by the broadcasts below, so it shows at once.
  // Only a list that failed to load needs a request.
  search.value = ''

  if (props.canListEvents && events.value.length === 0) {
    getEvents()
  }

  await nextTick()

  // Not on a touch screen: the keyboard would cover the list before anyone asked to search.
  if (window.matchMedia?.('(pointer: fine)').matches) {
    if (props.canListEvents) {
      searchInput.value?.focus()
    } else {
      ;(organisationRow.value?.$el ?? organisationRow.value)?.focus?.()
    }
  }
}

onClickOutside(panel, close, { ignore: [trigger] })

// The trigger moves between the rail and the open sidebar (or the phone drawer).
watch(() => props.collapsed, close)

const onKeydown = (keyboardEvent) =>
  keyboardEvent.key === 'Escape' && open.value && close()
let stopNavigateListener = null

/**
 * Loaded up front and refreshed on the same broadcasts as the classic header's dropdown, so opening
 * it never waits for the list. A refresh keeps whatever search is typed in.
 */
const COMPANY_EVENTS = ['.event:created', '.event:deleted', '.event:updated']
let companyChannel = null
let userChannel = null

onMounted(() => {
  stopNavigateListener = router.on('navigate', close)
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('resize', onResize)

  // Without event:list there is no list to load (events.search would refuse it).
  if (!props.canListEvents) {
    return
  }

  getEvents()

  const user = usePage().props._user
  companyChannel = useEcho().private(`companies.${user.active_company.id}`)
  COMPANY_EVENTS.forEach((name) => companyChannel.listen(name, getEvents))
  userChannel = useEcho()
    .private(`users.${user.id}`)
    .listen('.company:switched', getEvents)
})

onBeforeUnmount(() => {
  stopNavigateListener?.()
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', onResize)

  COMPANY_EVENTS.forEach((name) =>
    companyChannel?.stopListening(name, getEvents),
  )
  userChannel?.stopListening('.company:switched', getEvents)
})
</script>
